/**
 * Post Message Helper Functions for Visual Editor ↔ Preview Iframe Communication
 *
 * This module provides helper functions for sending type-safe messages between
 * the visual editor and the preview iframe.
 *
 * @example Basic Usage
 * ```typescript
 * import { previewMessage } from "@/lib/visual-editor/post-message";
 *
 * // Toggle selection
 * previewMessage.toggleSelection(iframeRef.current, true);
 *
 * // Update element
 * previewMessage.updateElement(iframeRef.current, updatedElement);
 *
 * // Navigate to route
 * previewMessage.navigateToRoute(iframeRef.current, "/about");
 * ```
 */

import type { PreviewMessage } from "@/types/preview-message.types";
import { PreviewMessageType } from "@/types/preview-message.types";
import type { ElementData } from "@/types/preview-message.types";

// Re-export types for convenience
export { PreviewMessageType } from "@/types/preview-message.types";
export type {
  PreviewMessage,
  PreviewMessageTypeValue,
  AddComponentMessage,
  DragMoveMessage,
  ElementUpdatedMessage,
  SelectElementByPathMessage,
  SelectParentMessage,
  CleanupTempElementsMessage,
  UndoAddMessage,
  RedoAddMessage,
  UndoDeleteMessage,
  RedoDeleteMessage,
  MoveElementMessage,
  HoverElementByPathMessage,
  ReorderElementsMessage,
} from "@/types/preview-message.types";

/**
 * Core function to send typed messages to preview iframe
 * @param iframe - The iframe element to send message to
 * @param message - The typed message to send
 * @param targetOrigin - Optional target origin (defaults to "*")
 */
function sendPreviewMessage(
  iframe: HTMLIFrameElement | null | undefined,
  message: PreviewMessage,
  targetOrigin: string = "*",
): void {
  if (!iframe?.contentWindow) {
    console.warn("[PostMessage] Preview iframe not available");
    return;
  }

  try {
    iframe.contentWindow.postMessage(message, targetOrigin);
  } catch (error) {
    console.error("[PostMessage] Failed to send message:", error);
  }
}

/**
 * Preview Message Helper Functions
 *
 * @example Usage
 * ```typescript
 * import { previewMessage } from "@/lib/visual-editor/postMessage";
 *
 * previewMessage.toggleSelection(iframeRef.current, true);
 * previewMessage.updateElement(iframeRef.current, updatedElement);
 * ```
 */
export const previewMessage = {
  /**
   * Send a raw message to preview iframe
   */
  send: sendPreviewMessage,

  /**
   * Toggle selection mode (enable/disable)
   */
  toggleSelection: (
    iframe: HTMLIFrameElement | null | undefined,
    enabled: boolean,
  ): void => {
    sendPreviewMessage(iframe, {
      type: enabled
        ? PreviewMessageType.ENABLE_SELECTION
        : PreviewMessageType.DISABLE_SELECTION,
    });
  },

  /**
   * Clear selection in preview
   */
  clearSelection: (iframe: HTMLIFrameElement | null | undefined): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.CLEAR_SELECTION,
    });
  },

  /**
   * Request layers from preview
   */
  requestLayers: (iframe: HTMLIFrameElement | null | undefined): void => {
    sendPreviewMessage(iframe, { type: PreviewMessageType.GET_LAYERS });
  },

  /**
   * Add a component to preview
   */
  addComponent: (
    iframe: HTMLIFrameElement | null | undefined,
    component: string,
    id: string,
    props: Record<string, any>,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.ADD_COMPONENT,
      component,
      id,
      props,
      "data-source": "visual-editor",
    });
  },

  /**
   * Duplicate an element in the preview iframe
   */
  duplicateElement: (
    iframe: HTMLIFrameElement | null | undefined,
    source: string,
    newId: string,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.DUPLICATE_ELEMENT,
      source,
      newId,
    });
  },

  /**
   * Send drag move update
   */
  dragMove: (
    iframe: HTMLIFrameElement | null | undefined,
    component: string,
    id: string,
    props: Record<string, any>,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.DRAG_MOVE,
      component,
      id,
      props,
      "data-source": "visual-editor",
    });
  },

  /**
   * Update element in preview
   */
  updateElement: (
    iframe: HTMLIFrameElement | null | undefined,
    updatedElement: ElementData,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.ELEMENT_UPDATED,
      updatedElement,
    });
  },

  /**
   * Select element by path
   */
  selectByPath: (
    iframe: HTMLIFrameElement | null | undefined,
    path: string,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.SELECT_ELEMENT_BY_PATH,
      path,
    });
  },

  /**
   * Select parent of currently selected element
   */
  selectParent: (iframe: HTMLIFrameElement | null | undefined): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.SELECT_PARENT,
    });
  },

  /**
   * Hover element by path
   */
  hoverByPath: (
    iframe: HTMLIFrameElement | null | undefined,
    path: string | null,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.HOVER_ELEMENT_BY_PATH,
      path,
    });
  },

  /**
   * Navigate to a route in the preview iframe
   */
  navigateToRoute: (
    iframe: HTMLIFrameElement | null | undefined,
    path: string,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.NAVIGATE_TO_ROUTE,
      path,
    });
  },

  /**
   * Update element in history (for undo/redo operations)
   * Uses ELEMENT_UPDATED type - same behavior as live updates
   */
  styleUpdateHistory: (
    iframe: HTMLIFrameElement | null | undefined,
    updatedElement: ElementData,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.ELEMENT_UPDATED,
      updatedElement,
    });
  },

  /**
   * Initialize theme in preview (call once on iframe load)
   * Fonts are cached in the iframe and used by subsequent updateTheme calls
   */
  initTheme: (
    iframe: HTMLIFrameElement | null | undefined,
    googleFonts: Record<string, string>,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.INIT_THEME,
      googleFonts,
    });
  },

  /**
   * Update theme in preview
   * Note: googleFonts should be initialized separately via initTheme()
   */
  updateTheme: (
    iframe: HTMLIFrameElement | null | undefined,
    themeId: string,
    content: string,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.UPDATE_THEME,
      themeId,
      content,
    });
  },

  /**
   * Reset theme in preview (clear optidev-override style)
   */
  resetTheme: (iframe: HTMLIFrameElement | null | undefined): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.RESET_THEME,
    });
  },

  /**
   * Set theme mode (light/dark) in preview
   */
  setThemeMode: (
    iframe: HTMLIFrameElement | null | undefined,
    mode: "light" | "dark",
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.SET_THEME_MODE,
      mode,
    });
  },

  /**
   * Inject Tailwind CDN into preview iframe for dynamic class support.
   * Enables any Tailwind utility class to work in the live preview,
   * even classes not present in the template source files.
   */
  injectTailwindCdn: (iframe: HTMLIFrameElement | null | undefined): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.INJECT_TAILWIND_CDN,
    });
  },

  /**
   * Navigate to a specific slide in the preview iframe
   */
  navigateToSlide: (
    iframe: HTMLIFrameElement | null | undefined,
    slideIndex: number,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.SLIDE_NAVIGATE,
      slideIndex,
    });
  },

  /**
   * Update presentation config in the preview iframe at runtime
   */
  updatePresentationConfig: (
    iframe: HTMLIFrameElement | null | undefined,
    config: Record<string, boolean | number | string>,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.PRESENTATION_CONFIG_UPDATE,
      config,
    });
  },

  /**
   * Update per-slide config override in the preview iframe
   */
  updatePerSlideConfig: (
    iframe: HTMLIFrameElement | null | undefined,
    slideId: string,
    config: Record<string, boolean | number | string | null>,
  ): void => {
    sendPreviewMessage(iframe, {
      type: PreviewMessageType.PER_SLIDE_CONFIG_UPDATE,
      slideId,
      config,
    });
  },
} as const;
